/**
 * Frontend UI renderer for the backend-deferred `ask_for_approval` tool.
 *
 * Like AskUserQuestionTool.tsx, this uses makeAssistantToolUI (UI only) — the
 * tool schema is defined in Python as an ExternalToolset (analysis-agent
 * interactive_ui_tools.py). The LLM calls the tool, pydantic-ai defers it,
 * this card collects the decision, and addResult() resumes the agent with the
 * literal "approved" / "denied" string the backend reads back.
 *
 * Self-contained on purpose: assistant-ui (assistant-v2) is legacy and slated
 * for wholesale removal, so it shares no code with the nextgen ApprovalCard.
 */
import { makeAssistantToolUI } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ToolCard } from "./ToolCard";

// Mirrors the backend `ask_for_approval` schema.
interface ApprovalMetadataRow {
  key: string;
  value: string;
}

interface ApprovalCardArgs {
  title: string;
  description?: string;
  icon?: string;
  metadata?: ApprovalMetadataRow[];
  variant?: "default" | "destructive";
  confirmLabel?: string;
  cancelLabel?: string;
}

const ApprovalCardRender: ToolCallMessagePartComponent<ApprovalCardArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const isRunning = status.type === "running";

  if (result != null) {
    const approved = result === "approved";
    return (
      <ToolCard label="Approval" color="rose" isLoading={false} loadingText="" isSubmitted={true}>
        {args?.title && <p className="font-medium text-sm">{args.title}</p>}
        <p className="text-xs font-medium">
          {approved ? (
            <span className="text-green-600">Approved</span>
          ) : (
            <span className="text-rose-600">Denied</span>
          )}
        </p>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      label="Approval"
      color="rose"
      isLoading={isRunning || !args}
      loadingText="Preparing approval..."
      isSubmitted={false}
    >
      {args?.title && <p className="font-medium text-sm">{args.title}</p>}
      {args?.description && <p className="text-xs text-muted-foreground">{args.description}</p>}

      {args?.metadata && args.metadata.length > 0 && (
        <dl className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm">
          {args.metadata.map((row) => (
            <div key={row.key} className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{row.key}</dt>
              <dd className="font-medium text-foreground">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={args?.variant === "destructive" ? "destructive" : "default"}
          onClick={() => addResult("approved")}
        >
          <Check className="size-3.5 mr-1" />
          {args?.confirmLabel ?? "Approve"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => addResult("denied")}>
          <X className="size-3.5 mr-1" />
          {args?.cancelLabel ?? "Deny"}
        </Button>
      </div>
    </ToolCard>
  );
};

export const ApprovalCardTool = makeAssistantToolUI<ApprovalCardArgs, string>({
  toolName: "ask_for_approval",
  render: ApprovalCardRender,
});
